"use client";

import { c as _c } from "react/compiler-runtime";
import { Badge } from "../ui";
import { useState } from "react";
import { RiFlagLine } from "react-icons/ri";
import { SingleProviderSelect } from "@/components/provider-select/single-select";
import { DurationInput } from "@/components/ui/duration-input";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { formatDate, formatStaffName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { TaskPriority } from "@/types/task";
import { useCarePlanContext } from "./care-plan";
import { formatMinutes } from "./step-detail-helpers";
import { type CarePlanStep, StepType, isPatientStep } from "./types";
import { useTeams } from "@/api/provider/teams/teams-query";
const PRIORITY_CONFIG: Record<TaskPriority, {
  label: string;
  color: string;
}> = {
  [TaskPriority.URGENT]: {
    label: "Urgent",
    color: "text-status-error-text"
  },
  [TaskPriority.HIGH]: {
    label: "High",
    color: "text-status-warning-text"
  },
  [TaskPriority.NORMAL]: {
    label: "Normal",
    color: "text-foreground-muted"
  },
  [TaskPriority.LOW]: {
    label: "Low",
    color: "text-accent-foreground"
  }
};
export function TaskStepDetail() {
  const $ = _c(37);
  const {
    state: t0,
    actions: t1,
    config: t2
  } = useCarePlanContext();
  const {
    selectedStep
  } = t0;
  const {
    updateStep
  } = t1;
  const {
    readOnly
  } = t2;
  const {
    data: t3
  } = useTeams();
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const teams = t4;
  if (!selectedStep || selectedStep.type !== StepType.TASK) {
    return null;
  }
  let t5;
  if ($[2] !== selectedStep.taskTeamId || $[3] !== teams) {
    t5 = selectedStep.taskTeamId ? teams.find(t => t.id === selectedStep.taskTeamId)?.name : undefined;
    $[2] = selectedStep.taskTeamId;
    $[3] = teams;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const selectedTeamName = t5;
  let t6;
  if ($[5] !== selectedStep.taskDescription) {
    t6 = selectedStep.taskDescription?.trim();
    $[5] = selectedStep.taskDescription;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const hasDescription = !!t6;
  let t7;
  if ($[7] !== readOnly || $[8] !== selectedStep.id || $[9] !== selectedStep.taskTitle || $[10] !== updateStep) {
    t7 = !readOnly && <div className="space-y-1"><Label>Task Title</Label><Input placeholder="Enter task title..." value={selectedStep.taskTitle ?? ""} onChange={e => updateStep(selectedStep.id, {
        taskTitle: e.target.value
      })} disabled={readOnly} /></div>;
    $[7] = readOnly;
    $[8] = selectedStep.id;
    $[9] = selectedStep.taskTitle;
    $[10] = updateStep;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== hasDescription || $[13] !== readOnly || $[14] !== selectedStep.id || $[15] !== selectedStep.taskDescription || $[16] !== updateStep) {
    t8 = (!readOnly || hasDescription) && <div className="space-y-1"><Label>Description</Label>{readOnly ? <p className="text-body text-foreground-secondary">{selectedStep.taskDescription ?? "Not set"}</p> : <Textarea placeholder="Task description..." value={selectedStep.taskDescription ?? ""} onChange={e_0 => updateStep(selectedStep.id, {
        taskDescription: e_0.target.value
      })} rows={3} />}</div>;
    $[12] = hasDescription;
    $[13] = readOnly;
    $[14] = selectedStep.id;
    $[15] = selectedStep.taskDescription;
    $[16] = updateStep;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Label>Priority</Label>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== readOnly || $[20] !== selectedStep.id || $[21] !== selectedStep.taskPriority || $[22] !== updateStep) {
    t10 = <div className="space-y-1">{t9}{readOnly ? <Badge>{selectedStep.taskPriority ? PRIORITY_CONFIG[selectedStep.taskPriority].label : "Not set"}</Badge> : <Select value={selectedStep.taskPriority ?? TaskPriority.NORMAL} onValueChange={v => updateStep(selectedStep.id, {
        taskPriority: v as TaskPriority
      })} disabled={readOnly}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.values(TaskPriority).map(_temp)}</SelectContent></Select>}</div>;
    $[19] = readOnly;
    $[20] = selectedStep.id;
    $[21] = selectedStep.taskPriority;
    $[22] = updateStep;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Label>Assignee</Label>;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== readOnly || $[26] !== selectedStep || $[27] !== selectedTeamName) {
    t12 = <div className="space-y-1">{t11}{readOnly ? <Badge>{selectedStep.taskTeamId ? selectedTeamName ? `Team: ${selectedTeamName}` : "Team" : selectedStep.taskAssigneeId ? formatStaffName(selectedStep.taskAssigneeId) : "Not set"}</Badge> : <AssigneeEditor key={selectedStep.id} step={selectedStep} />}</div>;
    $[25] = readOnly;
    $[26] = selectedStep;
    $[27] = selectedTeamName;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== selectedStep) {
    t13 = <TaskDueDate step={selectedStep} />;
    $[29] = selectedStep;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t12 || $[33] !== t13 || $[34] !== t7 || $[35] !== t8) {
    t14 = <div className="space-y-2">{t7}{t8}{t10}{t12}{t13}</div>;
    $[31] = t10;
    $[32] = t12;
    $[33] = t13;
    $[34] = t7;
    $[35] = t8;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
}
function _temp(priority) {
  const config = PRIORITY_CONFIG[priority];
  return <SelectItem key={priority} value={priority}><span className="flex items-center gap-2"><RiFlagLine className={cx("size-4", config.color)} />{config.label}</span></SelectItem>;
}
function AssigneeEditor(t0) {
  const $ = _c(20);
  const {
    step
  } = t0;
  const {
    actions: t1,
    config: t2
  } = useCarePlanContext();
  const {
    updateStep
  } = t1;
  const {
    readOnly
  } = t2;
  const {
    data: t3
  } = useTeams();
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const teams = t4;
  const [mode, setMode] = useState(step.taskTeamId ? "team" : "provider");
  let t5;
  if ($[2] !== step.id || $[3] !== updateStep) {
    t5 = value => {
      const next = value as "provider" | "team";
      setMode(next);
      if (next === "provider") {
        updateStep(step.id, {
          taskTeamId: undefined
        });
      } else {
        updateStep(step.id, {
          taskAssigneeId: undefined
        });
      }
    };
    $[2] = step.id;
    $[3] = updateStep;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TabsList className="grid w-full grid-cols-2"><TabsTrigger value="provider">Person</TabsTrigger><TabsTrigger value="team">Team</TabsTrigger></TabsList>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== mode || $[7] !== t5) {
    t7 = <Tabs value={mode} onValueChange={t5}>{t6}</Tabs>;
    $[6] = mode;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== mode || $[10] !== readOnly || $[11] !== step.id || $[12] !== step.taskAssigneeId || $[13] !== step.taskTeamId || $[14] !== teams || $[15] !== updateStep) {
    t8 = mode === "provider" ? <SingleProviderSelect value={step.taskAssigneeId} onValueChange={providerId => updateStep(step.id, {
      taskAssigneeId: providerId,
      taskTeamId: undefined
    })} placeholder="Select assignee..." disabled={readOnly} /> : <Select value={step.taskTeamId ?? ""} onValueChange={teamId => updateStep(step.id, {
      taskTeamId: teamId || undefined,
      taskAssigneeId: undefined
    })} disabled={readOnly}><SelectTrigger><SelectValue placeholder="Select team..." /></SelectTrigger><SelectContent>{teams.map(_temp2)}</SelectContent></Select>;
    $[9] = mode;
    $[10] = readOnly;
    $[11] = step.id;
    $[12] = step.taskAssigneeId;
    $[13] = step.taskTeamId;
    $[14] = teams;
    $[15] = updateStep;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="space-y-2">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function _temp2(team) {
  return <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>;
}
function TaskDueDate(t0) {
  const $ = _c(10);
  const {
    step
  } = t0;
  const {
    actions: t1,
    config: t2
  } = useCarePlanContext();
  const {
    updateStep
  } = t1;
  const {
    readOnly
  } = t2;
  let t3;
  if ($[0] !== readOnly || $[1] !== step || $[2] !== updateStep) {
    const approxDueDate = isPatientStep(step) && step.taskDueDateMinutes != null ? new Date(new Date(step.scheduledDate).getTime() + step.taskDueDateMinutes * 60000) : null;
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Label>Due Date</Label>;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== readOnly || $[6] !== step.id || $[7] !== step.taskDueDateMinutes || $[8] !== updateStep) {
      t5 = readOnly ? <Badge>{step.taskDueDateMinutes != null ? formatMinutes(step.taskDueDateMinutes) : "Not set"}</Badge> : <DurationInput value={step.taskDueDateMinutes} onChange={minutes => updateStep(step.id, {
        taskDueDateMinutes: minutes
      })} defaultUnit="days" placeholder="0" />;
      $[5] = readOnly;
      $[6] = step.id;
      $[7] = step.taskDueDateMinutes;
      $[8] = updateStep;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t3 = <div className="space-y-1">{t4}{t5}{approxDueDate && isPatientStep(step) && <p className="text-caption text-foreground-muted"><span>{step.isDateEstimated ? "~" : ""}</span>Due {formatDate(approxDueDate)}</p>}</div>;
    $[0] = readOnly;
    $[1] = step;
    $[2] = updateStep;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}